import { useState } from "react";

/**
 * Sortable score for sentiment objects: type hierarchy
 * (positive > mixed > negative > neutral) as primary, value as secondary.
 */
export function getSentimentSortScore(sentiment) {
    if (!sentiment) return 0;

    const typePriority = {
        positive: 4000,
        mixed: 3000,
        negative: 2000,
        neutral: 1000,
    };

    const categories = [
        { type: "positive", value: sentiment.positive ?? 0 },
        { type: "mixed", value: sentiment.mixed ?? 0 },
        { type: "negative", value: sentiment.negative ?? 0 },
        { type: "neutral", value: sentiment.neutral ?? 0 },
    ];

    const dominant = categories.reduce((prev, curr) =>
        curr.value > prev.value ? curr : prev
    );

    return (typePriority[dominant.type] ?? 0) + dominant.value;
}

export function sortResponses(responses, sortField, sortDirection) {
    if (!sortField) {
        // Default: question_position ascending, then answer_text ascending (case-insensitive).
        return [...responses].sort((a, b) => {
            const aPos = Number(a.question_position) || 0;
            const bPos = Number(b.question_position) || 0;
            if (aPos !== bPos) {
                return aPos - bPos;
            }
            const aText = a.answer_text.toLowerCase();
            const bText = b.answer_text.toLowerCase();
            return aText < bText ? -1 : aText > bText ? 1 : 0;
        });
    }
    return [...responses].sort((a, b) => {
        let aValue, bValue;
        switch (sortField) {
        case "question":
            aValue = Number(a.question_position) || 0;
            bValue = Number(b.question_position) || 0;
            break;
        case "answer_text":
            aValue = a.answer_text;
            bValue = b.answer_text;
            break;
        case "votes":
        case "matches":
            aValue = Number(a.times_chosen) || 0;
            bValue = Number(b.times_chosen) || 0;
            break;
        case "gobby_score":
            aValue = Number(a.gobby_score) || 0;
            bValue = Number(b.gobby_score) || 0;
            break;
        case "sentiment":
            aValue = getSentimentSortScore(a.sentiment);
            bValue = getSentimentSortScore(b.sentiment);
            break;
        case "theme_count":
            aValue = Number(a.theme_count) || 0;
            bValue = Number(b.theme_count) || 0;
            break;
        default:
            return 0;
        }
        if (typeof aValue === "string") {
            aValue = aValue.toLowerCase();
            bValue = bValue.toLowerCase();
        }
        if (sortDirection === "asc") {
            return aValue < bValue ? -1 : aValue > bValue ? 1 : 0;
        } else {
            return aValue > bValue ? -1 : aValue < bValue ? 1 : 0;
        }
    });
}

export function highlightKeyphrases(text, keyphrases, handleAddKeyword) {
    if (!keyphrases || keyphrases.length === 0) return text;
    const sorted = [...new Set(keyphrases)].sort((a, b) => b.length - a.length);
    let regex = new RegExp(
        `(${sorted
            .map((kp) => kp.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"))
            .join("|")})`,
        "gi",
    );
    return text.split(regex).map((part, i) =>
        sorted.some((kp) => kp.toLowerCase() === part.toLowerCase()) ? (
            <span
                key={i}
                onClick={(e) => handleAddKeyword(e)}
                className="bg-pink-200 text-pink-900 rounded px-1 mx-0.5"
            >
                {part}
            </span>
        ) : (
            part
        ),
    );
}

export const identity = (x) => x;
export const always = (x) => () => x;

export const sanitiseInput = (input) => input.trim().replace(/[<>"']/g, '');

/** Strip non-printable chars and normalize whitespace. Returns "" for falsy. */
export const sanitiseText = (text) => {
    if (typeof text !== 'string') return '';
    // eslint-disable-next-line no-control-regex
    return text.replace(/[\u0000-\u001F\u007F\u200B-\u200D\uFEFF]/g, '').replace(/\s+/g, ' ').trim();
};

export const useRateLimit = (maxAttempts = 3, blockDuration = 6000) => {
    const [attempts, setAttempts] = useState(0);
    const [blockedUntil, setBlockedUntil] = useState(0);

    const isBlocked = Date.now() < blockedUntil;

    const incrementAttempts = () => {
        setAttempts(prev => {
            const newAttempts = prev + 1;
            if (newAttempts >= maxAttempts) {
                setBlockedUntil(Date.now() + blockDuration);
                return 0; // reset after blocking
            }
            return newAttempts;
        });
    };

    const resetAttempts = () => {
        setAttempts(0);
        setBlockedUntil(0);
    };

    return { isBlocked, incrementAttempts, resetAttempts };
};
